import type { File, TextMaterial, TextMaterialTranslation, UpdateTextMaterialInput } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'
import { TEXT_CATEGORY } from '@hive/status'

import { mergeLeft } from 'merge-left-utils'

export interface TextMaterialFormState {
  id?: GQL_ID
  type_id?: GQL_ID
  category_id?: GQL_ID
  preview_id?: GQL_ID
  preview_source?: string
  preview_url?: string
  slug?: string
  status: string
  activeFrom?: Date
  activeUntil?: Date
  name: string
  title: string
  content: string
  i18n: TextMaterialTranslation[]
  languages: string[]
  preview?: File
  tagIds: GQL_ID[]
  themeIds: GQL_ID[]
  defaultLanguage: string
  priority_weight?: number
}

export const TEXT_MATERIAL_DEFAULT_STATE: TextMaterialFormState = {
  id: undefined,
  type_id: undefined,
  category_id: undefined,
  preview_id: undefined,
  preview: { url: undefined },
  preview_source: '',
  preview_url: '',
  slug: '',
  status: TEXT_CATEGORY.DISABLED,
  activeFrom: undefined,
  activeUntil: undefined,
  name: '',
  title: '',
  content: '',
  i18n: [],
  languages: [ 'ru', 'kk' ],
  tagIds: [],
  themeIds: [],
  defaultLanguage: 'ru',
  priority_weight: 0
}

export const makeTextMaterialFormState = (
  textMaterial?: TextMaterial
): TextMaterialFormState => {
  const activeLanguages = textMaterial?.i18n.map(({ languageCode }) => languageCode) || [ 'ru', 'kk' ]
  
  const newState =  mergeLeft<TextMaterialFormState>(TEXT_MATERIAL_DEFAULT_STATE, {
    ...textMaterial,
    themeIds: textMaterial?.themes?.map(({ id }) => id) || [],
    tagIds: textMaterial?.tags?.map(({ id }) => id) || [],
    activeFrom: textMaterial?.activeFrom ? new Date(textMaterial.activeFrom) : undefined,
    activeUntil: textMaterial?.activeUntil ? new Date(textMaterial.activeUntil) : undefined,
    languages: activeLanguages
  })
  
  return newState
}

export const mapStateToApiVariable = (state: TextMaterialFormState): UpdateTextMaterialInput => {
  const { languages, preview, defaultLanguage, ...rest } = state

  const defaultData = rest.i18n.find(translation => translation.languageCode === defaultLanguage) || { title: '', content: '', lead: '' }

  
  
  return {
    ...rest,
    defaultLanguage,
    title: defaultData.title,
    content: defaultData.content,
    lead: defaultData.lead,
    activeFrom: state.activeFrom?.getTime(), 
    activeUntil: state.activeUntil?.getTime(),
    priority_weight: Number(state.priority_weight) || 0,
  }
}

export const mapApiDataToState = (data: TextMaterial): TextMaterialFormState => {
  const defaultLanguage = data?.defaultLanguage
  const activeLanguages = data?.i18n.map(({ languageCode }) => languageCode).sort((a,b) => b === defaultLanguage ? 1 : -1) || [ 'ru', 'kk' ]

  return {
    ...data,
    themeIds: data.themes?.map(({ id }) => id) || [],
    type_id: data.type?.id,
    category_id: data.category?.id,
    preview_source: data.preview_source ?? '',
    tagIds: data.tags?.map(({ id }) => id) || [],
    preview_url: data.preview_url ?? '',
    defaultLanguage: data.defaultLanguage ?? activeLanguages[0],
    languages: activeLanguages ?? [ 'ru', 'kk' ],
    activeFrom: data.activeFrom ? new Date(data.activeFrom) : undefined,
    activeUntil: data.activeUntil ? new Date(data.activeUntil) : undefined,
    i18n: data?.i18n ? data.i18n.map(({ languageCode, title, content, lead }) => ({
      languageCode,
      title,
      content,
      lead
    })) : activeLanguages.map((languageCode) => ({
      languageCode,
      title: '',
      content: '',
      lead: ''
    }))
  }
}
